/**
 * @module @fanini/shared-api/provider
 * React Context Provider für den API-Client.
 */
import React, { createContext, useContext, useMemo } from 'react';
import { createApiClient, type ApiClient } from './client';

const ApiClientContext = createContext<ApiClient | null>(null);

/** Props für den ApiClientProvider */
export interface ApiClientProviderProps {
  /** Basis-URL der API (z.B. "https://api.fanini.live" oder "http://localhost:8787") */
  baseUrl: string;
  /** Funktion die den aktuellen Clerk JWT-Token zurückgibt */
  getToken: () => Promise<string | null>;
  children: React.ReactNode;
}

/**
 * React Context Provider der den API-Client allen Child-Komponenten bereitstellt.
 *
 * @param props - Provider-Konfiguration mit baseUrl und getToken
 * @returns Provider-Komponente
 *
 * @example
 * ```tsx
 * <ApiClientProvider
 *   baseUrl="https://api.fanini.live"
 *   getToken={() => clerk.session?.getToken() ?? Promise.resolve(null)}
 * >
 *   <App />
 * </ApiClientProvider>
 * ```
 */
export function ApiClientProvider({ baseUrl, getToken, children }: ApiClientProviderProps) {
  const client = useMemo(() => createApiClient(baseUrl, getToken), [baseUrl, getToken]);

  return <ApiClientContext.Provider value={client}>{children}</ApiClientContext.Provider>;
}

/**
 * Hook der den API-Client aus dem Context zurückgibt.
 * Muss innerhalb eines `ApiClientProvider` verwendet werden.
 *
 * @returns Typisierter API-Client
 * @throws Error wenn außerhalb eines ApiClientProvider verwendet
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const client = useApiClient();
 *   // client.get('/api/events')
 * }
 * ```
 */
export function useApiClient(): ApiClient {
  const client = useContext(ApiClientContext);
  if (!client) {
    throw new Error('useApiClient muss innerhalb eines <ApiClientProvider> verwendet werden.');
  }
  return client;
}
